import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { fetchProducts, updateProductRequest } from "../api/productsApi";

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const normalizeProduct = (product) => {
  const normalizedDetails = product.product_details
    ? {
        ...product.product_details,
        net_price: toNumber(product.product_details.net_price),
        vat_value: toNumber(product.product_details.vat_value),
        gross_price: toNumber(product.product_details.gross_price),
        vat_rate: toNumber(product.product_details.vat_rate),
      }
    : null;
  return {
    ...product,
    current_price: toNumber(product.current_price),
    available: Number.isFinite(Number(product.available)) ? Number(product.available) : 0,
    product: product.product ?? null,
    product_details: normalizedDetails,
  };
};

export const useProducts = () => {
  const { authToken, authFetch } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadProducts = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setMessage(null);
    try {
      const list = await fetchProducts(authToken);
      setProducts(list.map(normalizeProduct));
    } catch (error) {
      setProducts([]);
      setMessage({ kind: "error", text: error.message || "Failed to fetch product list." });
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  const refreshProducts = useCallback(() => loadProducts(), [loadProducts]);

  const updateProduct = useCallback(
    async (product, updates, successMessage) => {
      if (!authToken) throw new Error("Authentication required.");
      try {
        const data = await updateProductRequest({
          authFetch,
          productId: product.id,
          updates,
        });
        setProducts((prev) =>
          prev.map((item) => (item.id === product.id ? normalizeProduct(data) : item)),
        );
        setMessage({ kind: "success", text: successMessage });
        return data;
      } catch (error) {
        const text = error.message || "Failed to update the offer.";
        setMessage({ kind: "error", text });
        throw error;
      }
    },
    [authFetch, authToken],
  );

  useEffect(() => {
    if (!authToken) return;
    loadProducts();
  }, [authToken, loadProducts]);

  return {
    products,
    loading,
    message,
    setMessage,
    loadProducts,
    refreshProducts,
    updateProduct,
  };
};

export default useProducts;
